import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useGuardianLanguage } from '../context/GuardianLanguageContext';
import translations from '../i18n/translations';

const NAV_KEYS = [
  { id: 'dashboard', tKey: 'dashboard', icon: '▦', path: '/guardian/dashboard' },
  { id: 'reports',   tKey: 'reports',   icon: '◈', path: '/guardian/reports'   },
  { id: 'settings',  tKey: 'settings',  icon: '⚙', path: '/guardian/settings'  },
];

export default function GuardianSidebar({ guardianName, guardianEmail, onLogout }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { T } = useTheme();
  const { lang } = useGuardianLanguage();
  const tr = translations[lang];

  const [mobileOpen, setMobileOpen] = useState(false);
  const isRtl = lang === 'ar';

  useEffect(() => {
    if (!document.getElementById('jahbath-fonts')) {
      const l = document.createElement('link');
      l.id = 'jahbath-fonts';
      l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;600;700;800&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  const activeId =
    pathname.startsWith('/guardian/reports')  ? 'reports'  :
    pathname.startsWith('/guardian/settings') ? 'settings' :
    'dashboard';

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .gsb-aside {
            position: fixed !important;
            top: 0; bottom: 0;
            ${isRtl ? 'right: 0;' : 'left: 0;'}
            z-index: 1000;
            transform: translateX(${isRtl ? '100%' : '-100%'});
            transition: transform 0.25s ease, background 0.2s ease !important;
          }
          .gsb-aside.gsb-open {
            transform: translateX(0) !important;
          }
          .gsb-hamburger {
            display: flex !important;
          }
        }
        @media (min-width: 768px) {
          .gsb-hamburger { display: none !important; }
          .gsb-overlay   { display: none !important; }
        }
      `}</style>

      {/* Dark overlay — only rendered when open; @media hides it on desktop */}
      <div
        className="gsb-overlay"
        onClick={() => setMobileOpen(false)}
        style={{
          display: mobileOpen ? 'block' : 'none',
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 999,
        }}
      />

      {/* Hamburger button — hidden on desktop via @media */}
      <button
        className="gsb-hamburger"
        onClick={() => setMobileOpen(o => !o)}
        style={{
          display: 'none',
          position: 'fixed',
          top: 12,
          [isRtl ? 'left' : 'right']: 14,
          zIndex: 1001,
          width: 38, height: 38,
          alignItems: 'center', justifyContent: 'center',
          background: T.bgCardSolid,
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          fontSize: 18, cursor: 'pointer',
          color: T.textPrimary,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          fontFamily: 'inherit',
        }}
        aria-label="Toggle menu"
      >
        {mobileOpen ? '✕' : '☰'}
      </button>

      <aside
        className={`gsb-aside${mobileOpen ? ' gsb-open' : ''}`}
        style={{
          width: 240, flexShrink: 0, minHeight: '100vh',
          background: 'linear-gradient(180deg, #1E3A5F 0%, #0f2340 100%)',
          borderInlineEnd: '1px solid rgba(5,150,105,0.2)',
          display: 'flex', flexDirection: 'column',
          padding: '28px 0',
          fontFamily: "'Sora', sans-serif",
          transition: 'background 0.2s ease, border-color 0.2s ease',
        }}
      >
        <div
          onClick={() => {
            const token = localStorage.getItem('jahbath_token');
            if (token) {
              navigate('/guardian/dashboard');
            } else {
              window.location.href = '/jahbath-landing.html';
            }
            setMobileOpen(false);
          }}
          style={{
            padding: '0 24px 28px',
            borderBottom: `1px solid ${T.border}`,
            marginBottom: 8,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <img
            src="/jahbath-hat.png"
            alt="Jahbath"
            style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px' }}>
              <span style={{ color: '#ffffff' }}>Jah</span><span style={{ color: '#10B981' }}>bath</span>
            </div>
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
              {lang === 'ar' ? 'لوحة ولي الأمر' : 'Guardian Panel'}
            </div>
          </div>
        </div>

        <nav style={{ flex: 1 }}>
          {NAV_KEYS.map(n => {
            const active = activeId === n.id;
            return (
              <div
                key={n.id}
                onClick={() => { navigate(n.path); setMobileOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 24px', cursor: 'pointer',
                  background: active ? 'rgba(5,150,105,0.2)' : 'transparent',
                  borderInlineStart: active ? '3px solid #10B981' : '3px solid transparent',
                  borderRadius: active ? 10 : 0,
                  color: active ? '#10B981' : 'rgba(255,255,255,0.6)',
                  fontSize: 14, fontWeight: active ? 600 : 400,
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{ fontSize: 16 }}>{n.icon}</span>
                {tr[n.tKey]}
              </div>
            );
          })}
        </nav>

        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${T.border}`,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', fontFamily: "'Sora', sans-serif", marginBottom: 4 }}>
            {guardianName || '...'}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
            {guardianEmail || ''}
          </div>
          <button
            onClick={onLogout}
            style={{
              width: '100%', padding: '8px 0',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.5)', borderRadius: 10,
              cursor: 'pointer', fontSize: 13, fontWeight: 500,
              transition: 'all 0.2s ease', fontFamily: 'inherit',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.border = '1px solid rgba(239,68,68,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.border = '1px solid rgba(255,255,255,0.15)'; }}
          >
            {tr.logout}
          </button>
        </div>
      </aside>
    </>
  );
}
